import { localStorage } from "@prismicio/editor-support/DOM"
import { useHasBeenIdle } from "@prismicio/editor-support/React"
import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogDescription,
  DialogHeader,
  FormTextArea,
  RadioButton,
  RadioGroup,
  Toast,
} from "@prismicio/editor-ui"
import { useEffect, useState } from "react"

import { onboardingFeedback } from "../app/constants"
import {
  useOnboardingDropOffFlag,
  useOnboardingFeedbackFlag,
} from "../app/integrations/AmplitudeExperiments"
import {
  trackOnboardingFeedbackDialogVisible,
  trackOnboardingFeedbackToastVisible,
  trackOnboardingFeedbackValue,
} from "../app/services/tracking"
import type { OnboardingStep } from "./OnboardingSteps"

type OnboardingPageProps = {
  currentStep: OnboardingStep
}

/**
 * This a temporary experiment to collect feedback from users.
 *
 * Goal: capture real-time hesitation reasons when users linger
 * too long on the init screen without taking action.
 */
export function OnboardingFeedback(props: OnboardingPageProps) {
  const { currentStep } = props
  const onboardingDropOffFlag = useOnboardingDropOffFlag()
  const onboardingFeedbackFlag = useOnboardingFeedbackFlag()
  const idleTimeSeconds =
    onboardingFeedbackFlag?.payload?.idleTimeSeconds[onboardingDropOffFlag]
  const { hasBeenIdle } = useHasBeenIdle({
    idleTimeSeconds: idleTimeSeconds ?? 180,
  })
  const [feedbackToastOpen, setFeedbackToastOpen] = useState(false)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [feedback, setFeedback] = useState({
    value: "",
    reason: "",
  })

  useEffect(() => {
    const onboardingFeedbackFlagIsOn = onboardingFeedbackFlag?.value === "on"
    const alreadySawFeedbackToast = localStorage.has(onboardingFeedback)

    if (
      onboardingFeedbackFlagIsOn &&
      idleTimeSeconds &&
      !alreadySawFeedbackToast &&
      hasBeenIdle
    ) {
      trackOnboardingFeedbackToastVisible({ currentStep })
      setFeedbackToastOpen(true)
    }
  }, [hasBeenIdle, currentStep, onboardingFeedbackFlag, idleTimeSeconds])

  return (
    <>
      <Toast
        open={feedbackToastOpen}
        onOpenChange={(open) => setFeedbackToastOpen(open)}
        anchor={
          <Box
            position="fixed"
            width="100%"
            justifyContent="center"
            bottom={58}
          />
        }
        title="Something holding you back?"
        action={{
          onClick: () => {
            trackOnboardingFeedbackDialogVisible({ currentStep })
            localStorage.write(onboardingFeedback, "true")
            setFeedbackDialogOpen(true)
          },
          title: "Help us improve by telling us what's unclear.",
          icon: "openInNew",
        }}
      />
      <Dialog
        open={feedbackDialogOpen}
        onOpenChange={setFeedbackDialogOpen}
        size="small"
      >
        <DialogHeader title="Help us improve by telling us what's unclear." />
        <DialogContent>
          <DialogDescription asChild>
            <Box flexDirection="column" gap={8} padding={16}>
              <RadioGroup
                value={feedback.value}
                sx={{ flexGrow: 1 }}
                onChange={(value) =>
                  setFeedback((prevFeedback) => ({ ...prevFeedback, value }))
                }
              >
                <Box display="flex" flexDirection="column" gap={8}>
                  {onboardingFeedbackFlag?.payload?.options?.map((option) => (
                    <RadioButton
                      key={option.id}
                      value={option.id}
                      label={option.label}
                    />
                  ))}
                  <RadioButton value="something_else" label="Something else" />
                </Box>
              </RadioGroup>
              <FormTextArea
                placeholder="Type your reason..."
                value={feedback.reason}
                onChange={(reason) =>
                  setFeedback((prevFeedback) => ({ ...prevFeedback, reason }))
                }
                disabled={feedback.value !== "something_else"}
              />
            </Box>
          </DialogDescription>
        </DialogContent>
        <DialogActions>
          <DialogCancelButton
            onClick={() => setFeedbackDialogOpen(false)}
            size="small"
          >
            Dismiss
          </DialogCancelButton>
          <DialogActionButton
            onClick={() => {
              trackOnboardingFeedbackValue({
                currentStep,
                ...feedback,
              })
              setFeedbackDialogOpen(false)
            }}
            size="small"
            disabled={
              !feedback.value ||
              (!feedback.reason && feedback.value === "something_else")
            }
          >
            Send
          </DialogActionButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
